import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { loadEntry, roleWsEstimate, loadStatus, fmtH, fmtC, effectiveRate } from "@/lib/calc";
import { NumCell } from "@/components/estimation/cells";
import { Wand2, Plus, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const LOC_LABEL = { onshore: "Onshore", offshore: "Offshore" };
const LOC_CLS = {
  onshore: "bg-blue-50 text-[#002FA7]",
  offshore: "bg-emerald-50 text-emerald-700",
};

export default function LoadTab({ est, setEst, oppId }) {
  const n = Number(est.num_weeks) || 12;
  const weeks = Array.from({ length: n }, (_, i) => i);
  const tol = Number(est.tolerance_pct) || 0.05;
  const start = est.start_date ? new Date(est.start_date + "T00:00:00") : null;
  const [addingWs, setAddingWs] = useState(null);

  const weekLabel = (i) => {
    if (!start) return "";
    const d = new Date(start); d.setDate(d.getDate() + i * 7);
    return `${d.getMonth() + 1}/${d.getDate()}`;
  };

  const setCell = (ws, role, week, hours) => {
    setEst((prev) => ({
      ...prev,
      load: prev.load.map((e) =>
        e.workstream === ws && e.role === role
          ? { ...e, hours: e.hours.map((h, i) => (i === week ? Number(hours) || 0 : h)) }
          : e),
    }));
    api.put(`/opportunities/${oppId}/estimation/load-cell`, { workstream: ws, role, week, hours: Number(hours) || 0 })
      .catch((e) => toast.error(apiError(e)));
  };

  const setLocation = (ws, role, location) => {
    // Functional optimistic update — avoids stale-closure races across rapid row clicks.
    setEst((prev) => ({
      ...prev,
      load: prev.load.map((e) =>
        e.workstream === ws && e.role === role ? { ...e, location } : e),
    }));
    // Fire-and-forget; we deliberately do NOT replace state with the server response
    // to avoid clobbering concurrent in-flight updates on other rows.
    api.put(`/opportunities/${oppId}/estimation/load-row/location`,
      { workstream: ws, role, location })
      .catch((err) => toast.error(apiError(err)));
  };

  const addResource = async (ws, role) => {
    try {
      const { data } = await api.post(`/opportunities/${oppId}/estimation/load-row`,
        { workstream: ws, role });
      setEst(data);
      setAddingWs(null);
      toast.success(`${role} added to ${ws}`);
    } catch (err) { toast.error(apiError(err)); }
  };

  const removeResource = async (ws, role) => {
    if (!window.confirm(
      `Remove ${role} from ${ws}?\n\n• Weekly hours will be cleared\n• ${role}'s allocation will be stripped from every task in this workstream, so effort and cost will drop accordingly\n\nYou can re-add the resource later, but task allocations will need to be reset.`
    )) return;
    try {
      const { data } = await api.delete(`/opportunities/${oppId}/estimation/load-row`,
        { params: { workstream: ws, role } });
      setEst(data);
      toast.success(`${role} removed from ${ws} — effort & cost updated`);
    } catch (err) { toast.error(apiError(err)); }
  };

  const bulkSetLocation = async (scope, value, location) => {
    // scope: 'workstream' or 'role'
    const label = scope === "workstream" ? `workstream "${value}"` : `role "${value}"`;
    if (!window.confirm(`Set ${label} to ${location.toUpperCase()} for all matching rows?`)) return;
    // Build the list of (workstream, role) pairs to update
    const pairs = est.load.filter((e) =>
      scope === "workstream" ? e.workstream === value : e.role === value
    ).map((e) => ({ ws: e.workstream, role: e.role }));
    // Optimistic update
    setEst((prev) => ({
      ...prev,
      load: prev.load.map((e) => {
        const match = scope === "workstream" ? e.workstream === value : e.role === value;
        return match ? { ...e, location } : e;
      }),
    }));
    // Fire all PUTs in parallel; backend writes are atomic per-row
    try {
      await Promise.all(pairs.map(({ ws, role }) =>
        api.put(`/opportunities/${oppId}/estimation/load-row/location`, {
          workstream: ws, role, location,
        })
      ));
      toast.success(`Set ${pairs.length} row(s) to ${location}`);
    } catch (err) { toast.error(apiError(err)); }
  };

  const build = async () => {
    try {
      const { data } = await api.post(`/opportunities/${oppId}/estimation/load/build`);
      setEst(data);
      toast.success("Resource load rebuilt from estimate");
    } catch (e) { toast.error(apiError(e)); }
  };

  const syncWorkstream = async (ws) => {
    try {
      const { data } = await api.post(`/opportunities/${oppId}/estimation/load/sync-workstream`, { workstream: ws });
      setEst(data);
      toast.success(`"${ws}" load synced to WBS suggested`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveSettings = (patch) => {
    setEst({ ...est, ...patch });
    api.put(`/opportunities/${oppId}/estimation/settings`, patch)
      .then((r) => setEst(r.data))
      .catch((e) => toast.error(apiError(e)));
  };

  const weeklyTotals = weeks.map((w) =>
    (est.load || []).reduce((a, e) => a + (Number(e.hours[w]) || 0), 0));
  const grand = weeklyTotals.reduce((a, b) => a + b, 0);
  const rolesByName = Object.fromEntries(est.roles.map((r) => [r.name, r]));
  const grandCost = (est.load || []).reduce((a, e) => {
    const r = rolesByName[e.role];
    if (!r) return a;
    const hrs = (e.hours || []).reduce((x, y) => x + (Number(y) || 0), 0);
    return a + hrs * effectiveRate(r, e.location);
  }, 0);
  let grandEst = 0;
  for (const ws of est.workstreams) for (const r of est.roles) grandEst += roleWsEstimate(est, ws.name, r.name);
  const grandSt = loadStatus(grand, grandEst, tol);

  const th = "bg-zinc-50 border-b border-zinc-300 border-r border-r-zinc-200 px-2 py-2 text-[10px] uppercase tracking-wide text-zinc-500 font-medium whitespace-nowrap text-right";
  const stickyTh = "bg-zinc-50 border-b border-zinc-300 border-r border-r-zinc-200 px-3 py-2 text-[10px] uppercase tracking-wide text-zinc-500 font-medium text-left sticky left-0 z-10 min-w-[180px]";

  if (est.workstreams.length === 0)
    return <div data-testid="load-empty-state" className="border border-dashed border-zinc-300 rounded-sm p-12 text-center text-sm text-zinc-500">Build the WBS first — the resource load is derived from workstreams and role hours.</div>;

  return (
    <div data-testid="load-tab">
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <button data-testid="build-load-button" onClick={build}
          className="flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-medium px-4 py-2 rounded-sm">
          <Wand2 size={14} strokeWidth={1.5} /> Auto-build load from estimate
        </button>
        <div className="flex items-center gap-2 text-xs text-zinc-600">
          <label>Weeks</label>
          <input data-testid="num-weeks-input" type="number" min={1} max={52} defaultValue={n} key={`nw-${n}`}
            onBlur={(e) => { const v = Math.max(1, Math.min(52, Number(e.target.value) || n)); if (v !== n) saveSettings({ num_weeks: v }); }}
            className="w-14 border border-zinc-300 rounded-sm px-2 py-1 font-mono text-right outline-none focus:border-[#002FA7]" />
          <label className="ml-2">Start (Mon)</label>
          <input data-testid="start-date-input" type="date" defaultValue={est.start_date} key={`sd-${est.start_date}`}
            onBlur={(e) => e.target.value && e.target.value !== est.start_date && saveSettings({ start_date: e.target.value })}
            className="border border-zinc-300 rounded-sm px-2 py-1 font-mono outline-none focus:border-[#002FA7]" />
        </div>
        <span className="text-xs text-zinc-400 ml-auto">Status tolerance ±{Math.round(tol * 100)}% · variance = load − estimate (incl. contingency)</span>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-zinc-50 border border-zinc-200 rounded-sm">
        <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Bulk apply</span>
        <select
          data-testid="bulk-ws-select"
          defaultValue=""
          onChange={(e) => {
            const ws = e.target.value;
            if (!ws) return;
            const loc = window.prompt(`Set ALL roles in "${ws}" to which location?\n\nType one of: onshore | offshore`);
            if (!loc) { e.target.value = ""; return; }
            const lc = loc.trim().toLowerCase();
            if (!["onshore", "offshore"].includes(lc)) {
              toast.error("Location must be onshore or offshore");
              e.target.value = "";
              return;
            }
            bulkSetLocation("workstream", ws, lc);
            e.target.value = "";
          }}
          className="text-xs border border-zinc-300 rounded-sm px-2 py-1 bg-white outline-none focus:border-[#002FA7]">
          <option value="">Set workstream location…</option>
          {est.workstreams.map((ws) => <option key={ws.name} value={ws.name}>{ws.name}</option>)}
        </select>
        <select
          data-testid="bulk-role-select"
          defaultValue=""
          onChange={(e) => {
            const role = e.target.value;
            if (!role) return;
            const loc = window.prompt(`Set "${role}" to which location across ALL workstreams?\n\nType one of: onshore | offshore`);
            if (!loc) { e.target.value = ""; return; }
            const lc = loc.trim().toLowerCase();
            if (!["onshore", "offshore"].includes(lc)) {
              toast.error("Location must be onshore or offshore");
              e.target.value = "";
              return;
            }
            bulkSetLocation("role", role, lc);
            e.target.value = "";
          }}
          className="text-xs border border-zinc-300 rounded-sm px-2 py-1 bg-white outline-none focus:border-[#002FA7]">
          <option value="">Set role location everywhere…</option>
          {est.roles.map((r) => <option key={r.name} value={r.name}>{r.name}</option>)}
        </select>
        <div className="flex items-center gap-1.5 ml-2 text-[11px] text-zinc-500">
          <span>Quick:</span>
          {["onshore", "offshore"].map((loc) => (
            <button
              key={loc}
              data-testid={`bulk-all-${loc}`}
              onClick={() => {
                if (!window.confirm(`Set ALL rows across ALL workstreams to ${loc.toUpperCase()}?`)) return;
                // bulk: iterate every existing load entry
                const pairs = est.load.map((e) => ({ ws: e.workstream, role: e.role }));
                setEst((prev) => ({ ...prev, load: prev.load.map((e) => ({ ...e, location: loc })) }));
                Promise.all(pairs.map(({ ws, role }) =>
                  api.put(`/opportunities/${oppId}/estimation/load-row/location`, { workstream: ws, role, location: loc })
                )).then(() => toast.success(`All ${pairs.length} row(s) set to ${loc}`))
                  .catch((err) => toast.error(apiError(err)));
              }}
              className={`px-2 py-0.5 rounded-sm border text-[10px] uppercase tracking-wide font-medium ${LOC_CLS[loc]} border-transparent hover:border-zinc-400`}>
              All → {loc}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr>
              <th className={stickyTh}>Workstream / Role</th>
              <th className={`${th} text-center`}>Location</th>
              <th className={th}>Rate</th>
              {weeks.map((w) => <th key={w} className={th}>W{w + 1}<br /><span className="text-[9px] text-zinc-400 normal-case">{weekLabel(w)}</span></th>)}
              <th className={th}>Load</th><th className={th}>Cost</th><th className={th}>Estimate</th><th className={th}>Var</th><th className={th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {est.workstreams.map((ws) => {
              const rows = est.roles.map((r) => {
                const fallback = (r.default_location || "offshore").toLowerCase();
                const entry = loadEntry(est, ws.name, r.name) || { hours: [], location: fallback, visibility: "auto" };
                const total = entry.hours.reduce((a, b) => a + (Number(b) || 0), 0);
                const estimate = roleWsEstimate(est, ws.name, r.name);
                const rawLoc = (entry.location || "").toLowerCase();
                const location = rawLoc === "onshore" || rawLoc === "offshore" ? rawLoc : fallback;
                const visibility = entry.visibility || "auto";
                const rate = effectiveRate(r, location);
                return { roleObj: r, role: r.name, entry, total, estimate, location, visibility, rate, cost: total * rate };
              });
              // Visibility rules:
              //   visibility="shown"   → always show
              //   visibility="hidden"  → never show
              //   visibility="auto"    → show if there's any activity
              const visibleRows = rows.filter(({ visibility, estimate, total }) =>
                visibility === "shown" || (visibility !== "hidden" && (estimate > 0 || total > 0))
              );
              const hiddenOrAbsentRoles = rows.filter(({ visibility, estimate, total }) =>
                visibility === "hidden" || (visibility === "auto" && estimate === 0 && total === 0)
              ).map((r) => r.role);
              const subTotal = visibleRows.reduce((a, r) => a + r.total, 0);
              const subEst = visibleRows.reduce((a, r) => a + r.estimate, 0);
              const subCost = visibleRows.reduce((a, r) => a + r.cost, 0);
              const subSt = loadStatus(subTotal, subEst, tol);
              // Workstream-level drift vs the WBS suggested baseline (incl. contingency)
              const wsLoad = rows.reduce((a, r) => a + r.total, 0);
              const wsEst = rows.reduce((a, r) => a + r.estimate, 0);
              const wsSt = loadStatus(wsLoad, wsEst, tol);
              const wsDrift = wsEst > 0 && wsSt.label !== "MATCH";
              return [
                <tr key={ws.name} className="bg-zinc-100/70">
                  <td className="px-3 py-1.5 border-b border-zinc-200 text-xs font-medium sticky left-0 bg-zinc-100" colSpan={1}>{ws.name}</td>
                  <td colSpan={n + 6} className="border-b border-zinc-200 px-3">
                    {wsDrift && (
                      <div className="flex items-center justify-end gap-2">
                        <span className="text-[10px] text-zinc-500">
                          load {fmtH(wsLoad)} vs suggested {fmtH(wsEst)} (
                          <span className={wsLoad > wsEst ? "text-red-600 font-medium" : "text-amber-600 font-medium"}>
                            {wsLoad > wsEst ? "+" : ""}{fmtH(wsLoad - wsEst)}
                          </span>)
                        </span>
                        <button
                          data-testid={`sync-ws-${ws.name}`}
                          onClick={() => syncWorkstream(ws.name)}
                          title={`Scale ${ws.name} load to match its WBS suggested baseline (incl. contingency), preserving your weekly distribution`}
                          className="inline-flex items-center gap-1 text-[10px] font-medium text-[#002FA7] border border-[#002FA7]/30 hover:bg-[#002FA7]/5 rounded-sm px-2 py-0.5">
                          <RefreshCw size={11} strokeWidth={1.5} /> Sync load to suggested
                        </button>
                      </div>
                    )}
                  </td>
                </tr>,
                ...visibleRows.map(({ role, entry, total, estimate, location, rate, cost }) => {
                  const st = loadStatus(total, estimate, tol);
                  const v = total - estimate;
                  return (
                    <tr key={ws.name + role} className="hover:bg-zinc-50 group">
                      <td className="border-b border-r border-zinc-100 px-3 py-1 text-xs sticky left-0 bg-white group-hover:bg-zinc-50">
                        <div className="flex items-center justify-between gap-2">
                          <span>{role}</span>
                          <button
                            data-testid={`remove-resource-${ws.name}-${role}`}
                            onClick={() => removeResource(ws.name, role)}
                            title={`Remove ${role} from ${ws.name}`}
                            className="text-zinc-300 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity p-1">
                            <Trash2 size={12} strokeWidth={1.5} />
                          </button>
                        </div>
                      </td>
                      <td className="border-b border-r border-zinc-100 px-1 py-1 text-center">
                        <select
                          data-testid={`load-location-${ws.name}-${role}`}
                          value={location}
                          onChange={(e) => setLocation(ws.name, role, e.target.value)}
                          className={`text-[10px] font-medium uppercase tracking-wide rounded-sm px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-[#002FA7] ${LOC_CLS[location] || LOC_CLS.offshore}`}>
                          <option value="onshore">Onshore</option>
                          <option value="offshore">Offshore</option>
                        </select>
                      </td>
                      <td className="border-b border-r border-zinc-100 px-2 text-right font-mono text-xs text-zinc-600" data-testid={`load-rate-${ws.name}-${role}`}>
                        ${rate.toFixed(0)}
                      </td>
                      {weeks.map((w) => (
                        <td key={w} className="border-b border-r border-zinc-100 w-12">
                          <NumCell value={entry.hours[w] || null} placeholder="·"
                            onCommit={(val) => setCell(ws.name, role, w, val)}
                            testId={`load-cell-${ws.name}-${role}-${w}`} />
                        </td>
                      ))}
                      <td className="border-b border-r border-zinc-100 px-2 text-right font-mono text-xs font-medium">{fmtH(total)}</td>
                      <td className="border-b border-r border-zinc-100 px-2 text-right font-mono text-xs font-medium text-[#002FA7]" data-testid={`load-cost-${ws.name}-${role}`}>{fmtC(cost)}</td>
                      <td className="border-b border-r border-zinc-100 px-2 text-right font-mono text-xs text-zinc-500">{fmtH(estimate)}</td>
                      <td className={`border-b border-r border-zinc-100 px-2 text-right font-mono text-xs ${v > 0 ? "text-red-600" : v < 0 ? "text-amber-600" : "text-zinc-400"}`}>{v === 0 ? "0" : (v > 0 ? "+" : "") + fmtH(v)}</td>
                      <td className={`border-b border-zinc-100 px-2 text-right text-[10px] font-medium tracking-wide ${st.cls}`} data-testid={`load-status-${ws.name}-${role}`}>{st.label}</td>
                    </tr>
                  );
                }),
                <tr key={ws.name + "-add"} className="bg-white">
                  <td colSpan={3 + n + 5} className="border-b border-zinc-100 px-3 py-1.5 text-left">
                    {addingWs === ws.name ? (
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-wide text-zinc-500">Add resource:</span>
                        {hiddenOrAbsentRoles.length === 0 ? (
                          <span className="text-[11px] text-zinc-400 italic">All rate-card resources are already in this workstream.</span>
                        ) : (
                          hiddenOrAbsentRoles.map((roleName) => (
                            <button key={roleName}
                              data-testid={`add-resource-option-${ws.name}-${roleName}`}
                              onClick={() => addResource(ws.name, roleName)}
                              className="text-[11px] border border-zinc-300 hover:border-[#002FA7] hover:text-[#002FA7] rounded-sm px-2 py-0.5">
                              {roleName}
                            </button>
                          ))
                        )}
                        <button onClick={() => setAddingWs(null)} className="text-[11px] text-zinc-400 hover:text-zinc-700 ml-1">cancel</button>
                      </div>
                    ) : (
                      <button
                        data-testid={`add-resource-button-${ws.name}`}
                        onClick={() => setAddingWs(ws.name)}
                        className="inline-flex items-center gap-1 text-[11px] text-[#002FA7] hover:underline">
                        <Plus size={11} /> Add resource
                      </button>
                    )}
                  </td>
                </tr>,
                <tr key={ws.name + "-sub"} className="bg-zinc-50">
                  <td className="border-b border-r border-zinc-200 px-3 py-1 text-xs font-medium sticky left-0 bg-zinc-50">Subtotal</td>
                  <td className="border-b border-r border-zinc-200" />
                  <td className="border-b border-r border-zinc-200" />
                  {weeks.map((w) => {
                    const s = visibleRows.reduce((a, r) => a + (Number(r.entry.hours[w]) || 0), 0);
                    return <td key={w} className="border-b border-r border-zinc-200 px-1 text-right font-mono text-xs">{s || "·"}</td>;
                  })}
                  <td className="border-b border-r border-zinc-200 px-2 text-right font-mono text-xs font-medium">{fmtH(subTotal)}</td>
                  <td className="border-b border-r border-zinc-200 px-2 text-right font-mono text-xs font-medium text-[#002FA7]">{fmtC(subCost)}</td>
                  <td className="border-b border-r border-zinc-200 px-2 text-right font-mono text-xs">{fmtH(subEst)}</td>
                  <td className="border-b border-r border-zinc-200 px-2 text-right font-mono text-xs">{fmtH(subTotal - subEst)}</td>
                  <td className={`border-b border-zinc-200 px-2 text-right text-[10px] font-medium ${subSt.cls}`}>{subSt.label}</td>
                </tr>,
              ];
            })}
            <tr className="bg-zinc-900 text-white">
              <td className="px-3 py-2 text-xs font-medium sticky left-0 bg-zinc-900">GRAND TOTAL</td>
              <td className="px-2" />
              <td className="px-2" />
              {weeklyTotals.map((t, w) => <td key={`gt-w${w}`} className="px-1 text-right font-mono text-xs border-r border-zinc-700">{t || "·"}</td>)}
              <td className="px-2 text-right font-mono text-xs font-medium" data-testid="load-grand-total">{fmtH(grand)}</td>
              <td className="px-2 text-right font-mono text-xs font-medium text-emerald-300" data-testid="load-grand-cost">{fmtC(grandCost)}</td>
              <td className="px-2 text-right font-mono text-xs">{fmtH(grandEst)}</td>
              <td className="px-2 text-right font-mono text-xs">{fmtH(grand - grandEst)}</td>
              <td className={`px-2 text-right text-[10px] font-medium ${grandSt.label === "MATCH" ? "text-emerald-400" : grandSt.label === "OVER" ? "text-red-400" : "text-amber-400"}`} data-testid="load-grand-status">{grandSt.label}</td>
            </tr>
            <tr className="bg-zinc-50">
              <td className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-zinc-500 sticky left-0 bg-zinc-50">FTE @ {est.hours_per_week}h/wk</td>
              <td /><td />
              {weeklyTotals.map((t, w) => <td key={`fte-w${w}`} className="px-1 text-right font-mono text-[11px] text-zinc-500 border-r border-zinc-100">{t ? (t / (est.hours_per_week || 40)).toFixed(1) : "·"}</td>)}
              <td colSpan={5} />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
